/* eslint-disable react/forbid-prop-types */
/* eslint-disable max-len */
import React from 'react';
import moment from 'moment';
import { Link } from 'react-router-dom';
import PropTypes, { any } from 'prop-types';

import Img from '../../Img';
import { Utr, UtrLabel } from '../../text';
import Description from '../../text/Description';
import withAuth from '../../hocs/withAuth';
import BackgroundImage from '../../BackgroundImage';
import Stats from '../TeamStandingsCard/Stats';
import { titleCase } from '../../../utils/formatting';
import { MaximumMedalsToDisplay, sportTypeIds } from '../../../utils/eventConstants';
import PickleBallRating from '../PlayerCard/SharedComponents/PickleBallRating';
import { getPickleBallRating } from '../../../utils';
import { gameTypes, ratingTypes } from '../../../utils/constants';
import { getLabelByTypeId, hasRetiredTag } from '../../../utils/types';

import styles from './playerStandingsCardV3.scss';

function PlayerStandingsCardV3(props) {
    const {
        player,
        utrType,
        showDecimals,
        auth,
        showRatingNotLoggedIn,
        displayCollegeRecruitingValues,
        hideRatings,
        stats,
        childDrawStats,
        sportTypeId,
        drawMedal,
        divisionMedal,
        divisionName,
        isRoundRobin = false,
        isAdHoc = false,
        isDoubleElimination = false,
        isFeedInBackDraw = false,
        isFirstMatchBackDraw = false,
        standingsPosition,
        outcome = null,
        isIndexDisplayed = false,
        tagId,
        isPttEvent,
        drawTagTypes,
        'data-testid': dataTestId,
    } = props;

    if (!props.player) {
        return null;
    }

    const {
        gender,
        location,
        ratingStatusSingles,
        nationality,
        playerProfileImages,
        playerCollege,
        playerCollegeDetails,
        playerHighSchool,
        playerHighSchoolDetails,
        age,
        descriptionShort,
        descriptionLong,
        studentInfo,
    } = player || {};

    const firstName = titleCase(player.firstName);
    const lastName = titleCase(player.lastName);

    const userLoggedIn = auth?.user;
    const isPickleBallSportType = sportTypeId === sportTypeIds.PICKLEBALL;
    const shouldDisplayPlayoffColumns = isRoundRobin || isAdHoc;

    const nationalFlag = nationality ? `/img/flags/${nationality.toLowerCase()}.svg` : '';
    const playerPkbRating = getPickleBallRating(player, gameTypes.SINGLES);

    let description = '';
    if (descriptionLong) {
        description = [descriptionLong];
    } else {
        description = location?.display ? [location.display] : [];
    }

    let descriptionMobile = '';
    if (descriptionShort) {
        descriptionMobile = [descriptionShort];
    } else {
        descriptionMobile = location?.display ? [location.display] : [];
    }

    const descriptionItems = [
        ...(gender ? [gender.charAt(0)] : []),
        ...(description),
    ];

    let collegeRecruitingHighSchoolGradYear = null;

    if (studentInfo?.graduationDate) {
        collegeRecruitingHighSchoolGradYear = studentInfo.graduationDate;
    } else if (playerHighSchoolDetails?.gradYear) {
        collegeRecruitingHighSchoolGradYear = playerHighSchoolDetails.gradYear;
    }

    const descriptionItemsMobile = [
        ...(gender ? [gender.charAt(0)] : []),
        ...(displayCollegeRecruitingValues && age ? [`Age ${age}`] : []),
        ...(displayCollegeRecruitingValues && collegeRecruitingHighSchoolGradYear ? [`Grad. ${moment(collegeRecruitingHighSchoolGradYear).year().toString()}`] : []),
        ...(descriptionMobile),
    ];

    let clubCollegeDetailsMobile;

    if (playerCollege?.shortName) {
        if (playerCollegeDetails?.gradYear) {
            if (moment().diff(moment(playerCollegeDetails.gradYear)) < 0) {
                clubCollegeDetailsMobile = `${gender.charAt(0)} \u2022 ${playerCollege.shortName} ('${moment(playerCollegeDetails.gradYear).format('YY')})`;
            }
        }
    }
    if (playerHighSchool?.shortName && playerHighSchoolDetails?.gradYear) {
        // if player is currently in high school, show on mobile cards instead of location
        if (!clubCollegeDetailsMobile && moment().diff(moment(playerHighSchoolDetails.gradYear)) < 0) {
            let playerAgeAndStudentInfoGradYearString = '';

            if (displayCollegeRecruitingValues) {
                if (age) {
                    playerAgeAndStudentInfoGradYearString += `Age ${age} \u2022 `;
                }
                if (studentInfo?.graduationDate) {
                    playerAgeAndStudentInfoGradYearString += `Graduating ${moment(studentInfo.graduationDate).year().toString()} \u2022 `;
                } else {
                    playerAgeAndStudentInfoGradYearString += `Graduating ${moment(playerHighSchoolDetails.gradYear).year().toString()} \u2022 `;
                }
            }

            clubCollegeDetailsMobile = `${gender.charAt(0)} \u2022 ${playerAgeAndStudentInfoGradYearString}${playerHighSchool.shortName} ('${moment(playerHighSchoolDetails.gradYear).format('YY')})`;
        }
    }

    const showRatings = userLoggedIn || showRatingNotLoggedIn;

    const isWithdrawnPlayer = !!outcome;
    const isPlayerTagged = isPttEvent && !!tagId && hasRetiredTag(tagId);

    const getPlayersStatsSectionClassName = () => {
        const className = (isPlayerTagged || isWithdrawnPlayer) ? 'inactive' : '';
        if (isIndexDisplayed) {
            if (shouldDisplayPlayoffColumns) {
                return `col-md-8 ${className}`;
            }

            return `col-md-7 ${className}`;
        }

        if (shouldDisplayPlayoffColumns) {
            return `col-md-9 ${className}`;
        }

        return `col-md-8 ${className}`;
    };

    const renderRank = (drawMedal, standingsPosition, outcome, style, testIdPrefix = null) => {
        if (drawMedal && drawMedal <= MaximumMedalsToDisplay) {
            return (<Img src={`/img/medals/${drawMedal}.svg`} style={style} data-testid={testIdPrefix && `${testIdPrefix}.medal`} />);
        }

        if (isPlayerTagged) {
            return (<span className={styles.outcome} data-testid={testIdPrefix && `${testIdPrefix}.tag-label`}>{getLabelByTypeId(drawTagTypes, tagId)}</span>);
        }
        if (isWithdrawnPlayer) {
            return (<span className={styles.outcome} data-testid={testIdPrefix && `${testIdPrefix}.outcome`}>{outcome}</span>);
        }

        return (<span data-testid={testIdPrefix && `${testIdPrefix}.position`}>{standingsPosition}</span>);
    };

    return (
        <Link to={`/profiles/${player.id || player.playerId}`} target="_blank" data-testid={dataTestId && `${dataTestId}.card-link`}>
            <div className={`utr-card player-card player-card-v3 standings-card`}>
                <div className={`list-view d-none d-md-block`}>
                    <div className="d-flex">
                        <div className="flex-grow">
                            <div className="row">
                                {isIndexDisplayed && (
                                    <div className="col-1 d-flex justify-content-center br">
                                        <div className="rank" data-testid={dataTestId && `${dataTestId}.desktop.rank`}>
                                            {renderRank(drawMedal, standingsPosition, outcome, { width: '100%' }, dataTestId && `${dataTestId}.desktop.rank`)}
                                        </div>
                                    </div>
                                )}
                                <div className={`col-md-${shouldDisplayPlayoffColumns ? '3' : '4'} ${(isPlayerTagged || isWithdrawnPlayer) ? 'inactive' : ''}`}>
                                    <div className="avatar-wrapper">
                                        <BackgroundImage
                                            baseUrl={'/v1/player/'}
                                            defaultImage={'/img/v2/icons/icon-avatar-player.svg'}
                                            images={playerProfileImages}
                                            imageType={'icon'}
                                            parentStylesClass={'avatar d-inline-block'}
                                        />
                                        {nationalFlag && <Img className="flag" src={nationalFlag} alt={nationality} />}
                                    </div>
                                    <div className="name-and-place show-ellipsis">
                                        <div className="name show-ellipsis pointer" data-testid={dataTestId && `${dataTestId}.desktop.name`}>{firstName} {lastName}</div>
                                        <div className="place show-ellipsis" data-testid={dataTestId && `${dataTestId}.desktop.place`}>
                                            {divisionName ? (
                                                <div className="place show-ellipsis" data-testid={dataTestId && `${dataTestId}.desktop.division-medal`}>
                                                    {!!divisionMedal && <Img src={`/img/medals/${divisionMedal}.svg`} />}
                                                    {divisionName}
                                                </div>
                                            ) : (
                                                <Description desc={descriptionItems} data-testid={dataTestId && `${dataTestId}.desktop.place`} />
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className={getPlayersStatsSectionClassName()}>
                                    <div className="row w-100">
                                        {!hideRatings && (
                                            <div className={`col d-flex jcc aic`}>
                                                {showRatings && !isPickleBallSportType && (
                                                    <div className="values" data-testid={dataTestId && `${dataTestId}.desktop.singles-values`}>
                                                        <div
                                                            className={`value ${utrType === 'sanctioned' && ratingStatusSingles !== 'Rated' ? 'text-lighter' : ''}`}
                                                            title={utrType === 'sanctioned' ? ratingStatusSingles : ''}
                                                            data-testid={dataTestId && `${dataTestId}.desktop.singles-utr`}
                                                        >
                                                            <Utr player={player} force={{ ratingType: 'singles', utrType, verifiedIcon: true, showDecimals }} sportTypeId={sportTypeId} data-testid={dataTestId && `${dataTestId}.desktop.singles-utr`} />
                                                        </div>
                                                        <div className="title" data-testid={dataTestId && `${dataTestId}.desktop.singles-label`}>
                                                            <UtrLabel player={player} force={{ ratingType: 'singles', utrType, typeDots: true }} sportTypeId={sportTypeId} data-testid={dataTestId && `${dataTestId}.desktop.singles-label`} />
                                                        </div>
                                                    </div>
                                                )}
                                                {showRatings && isPickleBallSportType && (
                                                    <PickleBallRating player={player} ratingType={'singles'} data-testid={dataTestId && `${dataTestId}.desktop.singles-pickleball`} />
                                                )}
                                            </div>
                                        )}
                                        {shouldDisplayPlayoffColumns && (
                                            <Stats
                                                stats={childDrawStats}
                                                sportTypeId={sportTypeId}
                                                data-testid={dataTestId && `${dataTestId}.desktop.child-stats`}
                                            />
                                        )}
                                        {stats && (
                                            <Stats
                                                stats={stats}
                                                isDoubleElimination={isDoubleElimination}
                                                isFeedInBackDraw={isFeedInBackDraw}
                                                isFirstMatchBackDraw={isFirstMatchBackDraw}
                                                childDrawStats={childDrawStats}
                                                sportTypeId={sportTypeId}
                                                data-testid={dataTestId && `${dataTestId}.desktop.stats`}
                                            />
                                        )}
                                        {!stats && (
                                            <>
                                                <div className="col" />
                                                <div className="col" />
                                                <div className="col" />
                                                <div className="col" />
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className={`card-view d-block d-md-none ${styles.cardView} ${(isPlayerTagged || isWithdrawnPlayer) ? styles.inactive : ''} `}>
                    <div className="d-flex flex-row">
                        {isIndexDisplayed && (
                            <div className={`d-flex justify-content-center br`}>
                                <div className={styles.rank} data-testid={dataTestId && `${dataTestId}.mobile.rank`}>
                                    {renderRank(drawMedal, standingsPosition, outcome, { width: '30px', height: '30px' }, dataTestId && `${dataTestId}.mobile.rank`)}
                                </div>
                            </div>
                        )}
                        <div className={`d-flex flex-row ${(isPlayerTagged || isWithdrawnPlayer) ? 'inactive' : ''} ml8 pt8`}>
                            <div className="mr8">
                                <div className="avatar-container ml-auto">
                                    <BackgroundImage
                                        baseUrl={'/v1/player/'}
                                        defaultImage={'/img/v2/icons/icon-avatar-player.svg'}
                                        images={playerProfileImages}
                                        imageType={'icon'}
                                        parentStylesClass={`avatar ${styles.avatar}`}
                                    />
                                    {nationality && (
                                        <div className={`flag-container ${styles.flagContainer}`}>
                                            <Img
                                                className={`flag ${styles.flag}`}
                                                src={nationalFlag}
                                                alt={nationality}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="flex-1">
                                <div className={`${styles.details} show-ellipsis`}>
                                    <div className={`${styles.name} show-ellipsis`} data-testid={dataTestId && `${dataTestId}.mobile.name`}>
                                        {firstName} {lastName}
                                    </div>
                                    <div className={`${styles.description} show-ellipsis`}>
                                        {hideRatings ? (
                                            <Description desc={clubCollegeDetailsMobile || descriptionItemsMobile} data-testid={dataTestId && `${dataTestId}.mobile.place`} />
                                        ) : (
                                            <>
                                                {(userLoggedIn || showRatingNotLoggedIn) && !isPickleBallSportType && (
                                                    <div className={``}>
                                                        <span
                                                            className={`text-dark value ${utrType === 'sanctioned' && ratingStatusSingles !== 'Rated' ? 'text-lighter' : ''}`}
                                                            title={utrType === 'sanctioned' ? ratingStatusSingles : ''}
                                                            data-testid={dataTestId && `${dataTestId}.mobile.singles-utr`}
                                                        >
                                                            <Utr player={player} force={{ ratingType: 'singles', utrType, verifiedIcon: true, showDecimals }} sportTypeId={sportTypeId} data-testid={dataTestId && `${dataTestId}.mobile.singles-utr`} />
                                                        </span>
                                                        <span className="title" data-testid={dataTestId && `${dataTestId}.mobile.singles-label`}>
                                                            <UtrLabel player={player} force={{ ratingType: 'singles', utrType, typeDots: true }} sportTypeId={sportTypeId} data-testid={dataTestId && `${dataTestId}.mobile.singles-label`} />
                                                        </span>
                                                    </div>
                                                )}
                                                {(userLoggedIn || showRatingNotLoggedIn) && isPickleBallSportType && (
                                                    <div>
                                                        <span
                                                            className={`text-dark value ${utrType === 'sanctioned' ? 'text-lighter' : ''}`}
                                                            data-testid={dataTestId && `${dataTestId}.mobile.singles-pickleball`}
                                                        >
                                                            {(() => {
                                                                const ratingText = playerPkbRating.value ? ` (${ratingTypes.pickleball})` : '';
                                                                return `${playerPkbRating.value}${ratingText}`;
                                                            })()}
                                                        </span>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                    {!!divisionMedal && (
                                        <div className={`${styles.medal} show-ellipsis`} data-testid={dataTestId && `${dataTestId}.mobile.division-medal`}>
                                            <Img src={`/img/medals/${divisionMedal}.svg`} height={'14px'} />
                                            {divisionName}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                    {stats && (
                        <div className={`d-flex profile-details ${(isPlayerTagged || isWithdrawnPlayer) ? 'inactive' : ''} bt mt16 pl0 pr0 pb0`}>
                            <Stats
                                stats={stats}
                                force={{ cardView: true }}
                                sportTypeId={sportTypeId}
                                data-testid={dataTestId && `${dataTestId}.mobile.stats`}
                            />
                        </div>
                    )}
                </div>
            </div>
        </Link>
    );
}

PlayerStandingsCardV3.propTypes = {
    player: PropTypes.objectOf(any).isRequired,
    'data-testid': PropTypes.string,
    utrType: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
    ]),
    showDecimals: PropTypes.bool,
    auth: PropTypes.objectOf(any).isRequired,
    showRatingNotLoggedIn: PropTypes.bool,
    displayCollegeRecruitingValues: PropTypes.bool,
    hideRatings: PropTypes.bool,
    stats: PropTypes.arrayOf(any),
    childDrawStats: PropTypes.arrayOf(any),
    sportTypeId: PropTypes.number,
    drawMedal: PropTypes.number,
    divisionMedal: PropTypes.number,
    divisionName: PropTypes.string,
    isRoundRobin: PropTypes.bool,
    isAdHoc: PropTypes.bool,
    isDoubleElimination: PropTypes.bool,
    isFeedInBackDraw: PropTypes.bool,
    isFirstMatchBackDraw: PropTypes.bool,
    standingsPosition: PropTypes.bool,
    isIndexDisplayed: PropTypes.bool,
    outcome: PropTypes.string,
    tagId: PropTypes.string,
    isPttEvent: PropTypes.bool,
    drawTagTypes: PropTypes.arrayOf(any).isRequired,
};

PlayerStandingsCardV3.defaultProps = {
    'data-testid': null,
    utrType: null,
    showDecimals: false,
    showRatingNotLoggedIn: false,
    displayCollegeRecruitingValues: false,
    hideRatings: false,
    stats: [],
    childDrawStats: [],
    sportTypeId: sportTypeIds.TENNIS,
    drawMedal: null,
    divisionMedal: null,
    divisionName: '',
    isRoundRobin: false,
    isAdHoc: false,
    isDoubleElimination: false,
    isFeedInBackDraw: false,
    isFirstMatchBackDraw: false,
    standingsPosition: false,
    isIndexDisplayed: false,
    outcome: null,
    tagId: null,
    isPttEvent: false,
};

export default withAuth(PlayerStandingsCardV3);
